/**
 * Fixtures for the NEW BaseApi2 Compliance Doc Generator surface (DL-61574):
 * kebab-case routes under `/api/compliance-doc-generator/*`, the async
 * download flow (202 `{jobId}` + `/api/import-jobs/:jobId` poll) and the blob
 * download route `/api/compliance-document/:id/download`.
 *
 * Deliberately self-contained (local types, no `features/compliance/types`
 * imports) so the pre-polish develop types stay untouched — the FE punch-list
 * PR adopts these shapes into the feature types (DL-61362 / DL-60624).
 */

export type GeneratorStyle = {
  id: number;
  name: string;
  userDefinedId: string;
  brand: string;
  season: string;
  archived: boolean;
  /** Style preview-image URL; null on some styles so the picker's placeholder path is exercised. */
  imageUrl: string | null;
};

export type GeneratorCertFile = {
  certificateFileId: string;
  fileName: string;
  position: number;
};

export type GeneratorAvailableCert = {
  complianceId: number;
  source: string;
  sourceId: number;
  sourceName: string;
  /** Item branch: comma-aggregated category names. Style branch: null (live). */
  sourceCategory: string | null;
  certNumber: string;
  expiryDate: string | null;
  certState: string;
  certificateTypeName: string;
  /** Issuing bodies — AdminComplianceInstitute names, comma-aggregated (DL-61574 full-information pass). */
  institution: string | null;
  /** ISO issue date — from `Compliance.ValidDate` (valid-from ≡ issue date). */
  issueDate: string | null;
  isAccessible: boolean;
  isDisabled: boolean;
  disabledReason: string | null;
  files: GeneratorCertFile[];
};

export type GeneratorTemplate = {
  id: number;
  name: string;
  /** Number of `[items:]` blocks in the template body (DL-61812 Stage 1). */
  itemBlockCount: number;
};

/** One linked BoM item — GET /api/compliance-doc-generator/style-items (DL-61812 Stage 1). */
export type GeneratorStyleItem = {
  id: number;
  name: string;
  userDefinedId: string;
  description: string | null;
  quality: string | null;
  physicalReferenceNumber: string | null;
  suppliedBy: string | null;
  erpItemId: string | null;
  category: string | null;
  position: number;
  thumbnailUrl?: string | null;
};

/** One `[items:]` block on the preview response — one rendered line per item in the bound group. */
export type GeneratorPreviewItemsBlock = {
  blockIndex: number;
  lines: string[];
};

export type GeneratorTemplateField = {
  id: number;
  fieldName: string;
  placeHolderPattern: string;
};

export type GeneratorJobWarning = {
  type: string;
  field: string;
  /** Offending certificateFile GUID; null for non-file-specific warnings. */
  value: string | null;
};

/** The only style with available certs in the mock (others → empty data). */
export const GENERATOR_CERTS_STYLE_ID = 9025;

/** In-org style whose linked items are ALL deactivated: style-items serves an EMPTY active-only
 * list with `inactiveLinkedItemCount` > 0, driving the "activate your items" empty state. */
export const GENERATOR_INACTIVE_ITEMS_STYLE_ID = 9024;
/** Linked-but-deactivated count served for {@link GENERATOR_INACTIVE_ITEMS_STYLE_ID}. */
export const GENERATOR_INACTIVE_ITEMS_COUNT = 3;

/** Deterministic trigger values for the download/poll error branches. */
export const GENERATOR_DOWNLOAD_TRIGGERS = {
  /** templateId → 404 `entity_not_found` on POST download. */
  templateNotFound: 66404,
  /** templateId → 202 whose job then polls to `Failed`. */
  jobWillFail: 66500,
};

/** jobId returned by the happy 202 (polls Queued → Processing → Completed). */
export const GENERATOR_JOB_ID = 7001;
/** jobId returned when `jobWillFail` triggered (polls to `Failed`). */
export const GENERATOR_FAILED_JOB_ID = 7002;
/** jobId owned by another user → 403 `forbidden` on poll. */
export const GENERATOR_FORBIDDEN_JOB_ID = 7003;

/** complianceDocumentId produced by the Completed job (blob route 200). */
export const GENERATOR_DOCUMENT_ID = 501;
/** documentId still generating → 409 `document_not_ready` on blob route. */
export const GENERATOR_DOCUMENT_NOT_READY_ID = 502;

/**
 * Saved-document FileName — mirrors the BE's BuildFileName composition
 * `{StyleName} - {TemplateName} - {dd-MMM-yyyy}.pdf` for the happy download
 * (style 9025 × template 11, created 13-Jul-2026). Served in the import-job
 * payload AND the blob route's Content-Disposition so both read identically.
 */
export const GENERATOR_SAVED_FILE_NAME = `Style ${GENERATOR_CERTS_STYLE_ID} - Standard compliance summary - 13-Jul-2026.pdf`;

/** Content-Disposition name served by POST /preview-pdf (DL-61574 V2). */
export const GENERATOR_PREVIEW_FILE_NAME = 'Standard compliance summary - preview.pdf';

/** Tiny inert 1×1 gif — a harmless stand-in for a style image. jsdom never loads it; the picker
 * asserts the `<img>` element/attrs, not a decoded bitmap. */
const STYLE_THUMBNAIL_URL = 'data:image/gif;base64,R0lGODlhAQABAAAAACwAAAAAAQABAAA=';
/** Item thumbnail fixture — same 1x1 gif; only presence/absence matters to the picker. */
const ITEM_THUMBNAIL_URL = STYLE_THUMBNAIL_URL;

/** 25 styles → exercises keyset pagination (20/page, hasMore on page 1). Even-indexed styles carry an
 * `imageUrl`, odd-indexed serve null, so both the image and placeholder paths render. */
export const generatorStyles: GeneratorStyle[] = Array.from({ length: 25 }, (_, i) => {
  const id = 9001 + i;
  return {
    id,
    name: `Style ${id}`,
    userDefinedId: `STY-${id}`,
    brand: i % 2 === 0 ? 'Nordkap' : 'Aurora',
    season: i % 3 === 0 ? 'SS26' : 'AW25',
    archived: i % 7 === 0,
    imageUrl: i % 2 === 0 ? STYLE_THUMBNAIL_URL : null,
  };
});

/**
 * Live-BE cert shape: `source`/`certState` as enum NAME strings,
 * `certificateTypeName` joined from CertificateTypeMaster, and the
 * `'Pending approval'` disabled reason (precedence: Expired > Pending
 * approval > Inaccessible). Cert-file ids stay GUID strings; `position`
 * is 1-based.
 */
export const generatorAvailableCerts: GeneratorAvailableCert[] = [
  {
    complianceId: 3101,
    source: 'Style',
    sourceId: GENERATOR_CERTS_STYLE_ID,
    sourceName: `Style ${GENERATOR_CERTS_STYLE_ID}`,
    // Style-branch certs carry NO category on live (Item branch only).
    sourceCategory: null,
    certNumber: 'GOTS-2026-0142',
    expiryDate: '2027-03-01T00:00:00Z',
    certState: 'Valid',
    certificateTypeName: 'GOTS',
    institution: 'Control Union, Ecocert',
    issueDate: '2026-02-15T00:00:00Z',
    isAccessible: true,
    isDisabled: false,
    disabledReason: null,
    files: [
      {
        certificateFileId: 'a1b2c3d4-0001-4a2b-9c3d-000000000001',
        fileName: 'gots-certificate.pdf',
        position: 1,
      },
      {
        certificateFileId: 'a1b2c3d4-0001-4a2b-9c3d-000000000002',
        fileName: 'gots-scope-annex.pdf',
        position: 2,
      },
    ],
  },
  {
    complianceId: 3102,
    source: 'Item',
    sourceId: 4410,
    sourceName: 'Organic cotton twill',
    sourceCategory: 'Fabric',
    certNumber: 'OEKO-11-55821',
    expiryDate: '2026-01-15T00:00:00Z',
    certState: 'Expired',
    certificateTypeName: 'OEKO-TEX Standard 100',
    institution: 'Hohenstein Institute',
    issueDate: '2025-01-16T00:00:00Z',
    isAccessible: true,
    isDisabled: true,
    disabledReason: 'Expired',
    files: [
      {
        certificateFileId: 'a1b2c3d4-0002-4a2b-9c3d-000000000001',
        fileName: 'oeko-tex-11-55821.pdf',
        position: 1,
      },
    ],
  },
  {
    complianceId: 3103,
    source: 'Item',
    sourceId: 4411,
    sourceName: 'Recycled polyester lining',
    sourceCategory: 'Fabric',
    certNumber: 'GRS-2026-7710',
    expiryDate: '2027-06-30T00:00:00Z',
    certState: 'Draft',
    certificateTypeName: 'GRS',
    // Degrade case: no institute mapped, no valid-from date.
    institution: null,
    issueDate: null,
    isAccessible: true,
    isDisabled: true,
    disabledReason: 'Pending approval',
    files: [
      {
        certificateFileId: 'a1b2c3d4-0003-4a2b-9c3d-000000000001',
        fileName: 'grs-transaction-cert.pdf',
        position: 1,
      },
    ],
  },
  {
    // Live `source` is ComplianceEntityType = Style | Item only — there is no
    // Supplier branch on this endpoint.
    complianceId: 3104,
    source: 'Item',
    sourceId: 780,
    sourceName: 'Corozo buttons',
    sourceCategory: 'Trims',
    certNumber: 'BSCI-2025-3301',
    expiryDate: '2026-11-01T00:00:00Z',
    certState: 'Passed',
    certificateTypeName: 'amfori BSCI',
    institution: null,
    issueDate: null,
    isAccessible: false,
    isDisabled: true,
    disabledReason: 'Inaccessible',
    files: [],
  },
];

/**
 * `{id,name,itemBlockCount}` ONLY — the live route never serializes template `Content`;
 * `itemBlockCount` is derived server-side by `ItemsBlockMatcher.CountBlocks` (DL-61812) and
 * decides whether the BOM Items wizard step appears at all.
 *
 * Only template 13 carries item blocks. 11 and 12 stay at 0 deliberately: 12 is the template
 * the pre-existing wizard walk-through tests drive, and giving it blocks would silently
 * insert a step into every one of those flows.
 */
export const generatorTemplates: GeneratorTemplate[] = [
  { id: 11, name: 'Standard compliance summary', itemBlockCount: 0 },
  { id: 12, name: 'Full audit pack', itemBlockCount: 0 },
  { id: 13, name: 'Customs declaration cover', itemBlockCount: 2 },
];

/** Template with NO `[items:]` blocks — the 5-step flow (no BOM Items step). */
export const GENERATOR_TEMPLATE_NO_ITEM_BLOCKS = 12;
/** Template with TWO `[items:]` blocks — the 6-step flow with group tabs. */
export const GENERATOR_TEMPLATE_ITEM_BLOCKS = 13;
export const GENERATOR_TEMPLATE_ITEM_BLOCK_COUNT = 2;

/**
 * GET /api/compliance-doc-generator/style-items rows (DL-61812 Stage 1) — the style's
 * deduped BoM items in `position` order. Only `GENERATOR_CERTS_STYLE_ID` has items; every
 * other style answers an EMPTY list, mirroring the live SQL tenant filter (a cross-org or
 * unknown style is indistinguishable from a style with no BoM).
 */
export const generatorStyleItems: GeneratorStyleItem[] = [
  {
    id: 8101,
    name: 'Organic cotton jersey 180gsm',
    userDefinedId: 'FAB-0101',
    description: 'Main body fabric',
    quality: '100% organic cotton',
    physicalReferenceNumber: 'PR-8101',
    suppliedBy: 'Nordic Textiles',
    erpItemId: 'ERP-8101',
    category: 'Fabric',
    position: 1,
    // Only the first item carries an image, so the picker renders both the image and the
    // placeholder path in one list.
    thumbnailUrl: ITEM_THUMBNAIL_URL,
  },
  {
    id: 8102,
    name: 'Recycled polyester lining',
    userDefinedId: 'FAB-0102',
    description: 'Body lining',
    quality: '100% rPET',
    physicalReferenceNumber: 'PR-8102',
    suppliedBy: 'Nordic Textiles',
    erpItemId: null,
    category: 'Fabric, Lining',
    position: 2,
  },
  {
    id: 8103,
    name: 'Corozo button 18L',
    userDefinedId: 'TRM-0210',
    description: null,
    quality: null,
    physicalReferenceNumber: null,
    suppliedBy: 'Baltic Trims',
    erpItemId: null,
    // Live emits null for an uncategorized item (not an empty string).
    category: null,
    position: 3,
  },
  {
    id: 8104,
    name: 'FSC cardboard hangtag',
    userDefinedId: 'PKG-0044',
    description: 'PPWR-relevant packaging',
    quality: 'FSC Mix',
    physicalReferenceNumber: 'PR-8104',
    suppliedBy: 'GreenPack',
    erpItemId: 'ERP-8104',
    category: 'Packaging',
    position: 4,
  },
];

/** Real BE field names; Divider excluded; `{pageBreak}` last (live filtering). */
export const generatorTemplateFields: GeneratorTemplateField[] = [
  { id: 1, fieldName: 'Style name', placeHolderPattern: '{styleName}' },
  { id: 2, fieldName: 'Style number', placeHolderPattern: '{styleNumber}' },
  { id: 3, fieldName: 'Brand', placeHolderPattern: '{brand}' },
  { id: 4, fieldName: 'Season', placeHolderPattern: '{season}' },
  { id: 5, fieldName: 'Certificate list', placeHolderPattern: '{certificateList}' },
  { id: 6, fieldName: 'Page break', placeHolderPattern: '{pageBreak}' },
];

export type GeneratorPreviewField = {
  fieldId: number;
  fieldName: string;
  placeHolderPattern: string;
  value: string | null;
  isMissing: boolean;
};

export type GeneratorPreview = {
  templateId: number;
  complianceId: number | null;
  styleId: number;
  templateName: string;
  styleName: string;
  fields: GeneratorPreviewField[];
  itemsBlocks: GeneratorPreviewItemsBlock[];
};

/**
 * POST /api/compliance-doc-generator/preview rows (live shape, DL-61574): flat
 * per-field rows — resolved value + `isMissing` when the resolver fell back to
 * the no-data dash. `{pageBreak}` is present ON the wire (the FE adapter drops
 * it). 4 complete + 3 missing data rows → the readiness step reads
 * "4 of 7 fields ready" with 3 missing.
 */
export const generatorPreviewFields: GeneratorPreviewField[] = [
  {
    fieldId: 1,
    fieldName: 'Style name',
    placeHolderPattern: '{styleName}',
    value: 'Coastal Breeze Linen Shirt',
    isMissing: false,
  },
  {
    fieldId: 2,
    fieldName: 'Style number',
    placeHolderPattern: '{styleNumber}',
    value: 'SS26-2087',
    isMissing: false,
  },
  {
    fieldId: 3,
    fieldName: 'Certificate name',
    placeHolderPattern: '{certificateName}',
    value: 'OEKO-TEX Standard 100',
    isMissing: false,
  },
  {
    fieldId: 4,
    fieldName: 'Certificate number',
    placeHolderPattern: '{certificateNumber}',
    value: '-',
    isMissing: true,
  },
  {
    fieldId: 5,
    fieldName: 'Institute name',
    placeHolderPattern: '{instituteName}',
    value: 'Intertek',
    isMissing: false,
  },
  {
    fieldId: 6,
    fieldName: 'Standard name',
    placeHolderPattern: '{standardName}',
    value: '-',
    isMissing: true,
  },
  // Custom-field pattern (NOT in the static token union — the adapter carries no fieldKey).
  {
    fieldId: 7,
    fieldName: 'Remark',
    placeHolderPattern: '{CF:Remark}',
    value: '-',
    isMissing: true,
  },
  {
    fieldId: 8,
    fieldName: 'Page break',
    placeHolderPattern: '{pageBreak}',
    value: 'Page break',
    isMissing: false,
  },
];

/** Full live preview payload; `complianceId` echoes the command (null = anchorless). */
export const generatorPreview = (
  complianceId: number | null,
  templateId = 11,
  // Mirrors the wire body, where an inner group can be null — the handler's validateItemGroups
  // has already rejected that with a 400 by the time we render, so it only needs tolerating.
  itemGroups?: (number[] | null)[] | null
): GeneratorPreview => {
  const template = generatorTemplates.find(candidate => candidate.id === templateId);
  return {
    templateId,
    complianceId,
    styleId: GENERATOR_CERTS_STYLE_ID,
    templateName: template?.name ?? 'Standard compliance summary',
    styleName: 'Coastal Breeze Linen Shirt',
    fields: generatorPreviewFields,
    // BE-10 Stage 2 semantics (DL-61967): one entry per `[items:]` block, carrying EXACTLY one
    // line per item in that block's group — a missing value degrades to a dash rather than
    // dropping the row. A block with no bound group keeps the empty-lines shape.
    itemsBlocks: Array.from({ length: template?.itemBlockCount ?? 0 }, (_, blockIndex) => ({
      blockIndex,
      lines: (itemGroups?.[blockIndex] ?? []).map(itemId => {
        const item = generatorStyleItems.find(candidate => candidate.id === itemId);
        return `${item?.userDefinedId ?? '-'} · ${item?.name ?? '-'}`;
      }),
    })),
  };
};

/**
 * skip-and-warn warnings inside the Completed job's `result`. Live shape is
 * `MergeWarning.Of(type, certificateFileId)`: `type` is one of the six
 * `merge_failed_*` codes, `field` is always `'certificateFileIds'` and
 * `value` is the offending file GUID (null for non-file-specific warnings).
 */
export const generatorJobWarnings: GeneratorJobWarning[] = [
  {
    type: 'merge_failed_corrupt_pdf',
    field: 'certificateFileIds',
    value: 'a1b2c3d4-0002-4a2b-9c3d-000000000001',
  },
];

/** Completed job `result` — parsed ResultSummaryJson on the live route. */
export const generatorJobResult = {
  complianceDocumentId: GENERATOR_DOCUMENT_ID,
  warnings: generatorJobWarnings,
};

/**
 * Failed job `error` — the live route returns ErrorMessage verbatim as a
 * string, and `MarkJobFailed` writes `SerializeWarnings(warnings)`: a JSON
 * ARRAY of MergeWarning rows (lowercase `type`/`field`/`value` keys), never
 * a `{code,message}` object.
 */
export const generatorJobError = JSON.stringify([
  { type: 'merge_payload_too_large', field: 'certificateFileIds', value: null },
]);

/**
 * Builds the exact `ImportJobStatusController.GetStatus` payload (camelCase
 * anonymous-object shape, `state` as enum NAME string).
 */
export const generatorImportJob = (
  jobId: number,
  state: 'Queued' | 'Processing' | 'Completed' | 'Failed'
) => ({
  id: jobId,
  hangfireJobId: `hf-${jobId}`,
  importType: 'ComplianceDocGeneration',
  fileName: GENERATOR_SAVED_FILE_NAME,
  fileSize: 148_112,
  state,
  createdAt: '2026-07-13T08:00:00Z',
  startedAt: state === 'Queued' ? null : '2026-07-13T08:00:02Z',
  completedAt: state === 'Completed' || state === 'Failed' ? '2026-07-13T08:00:09Z' : null,
  progress: {
    totalRows: 1,
    processedRows: state === 'Completed' || state === 'Failed' ? 1 : 0,
    createdRows: state === 'Completed' ? 1 : 0,
    updatedRows: 0,
    affectedRows: state === 'Completed' ? 1 : 0,
    errorRows: state === 'Failed' ? 1 : 0,
    percentComplete: state === 'Completed' || state === 'Failed' ? 100 : 0,
  },
  result: state === 'Completed' ? generatorJobResult : null,
  error: state === 'Failed' ? generatorJobError : null,
});

/**
 * Minimal parseable PDF as a STRING body — Pretender/jsdom cannot round-trip
 * ArrayBuffer bodies, so the mock (like `mockPdfBody`) serves text + headers.
 */
export const generatorPdfBody = () =>
  '%PDF-1.4\n1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\ntrailer\n<< /Root 1 0 R >>\n%%EOF';
